/**
 * Voice Assistant Panel Component
 * Left-side panel with auto-start listening and pause/send functionality
 * Integrated with Car Doctor Voice Assistant WebSocket
 */

import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Pause, Play, Send, Volume2, VolumeX, X, WifiOff } from 'lucide-react';
import './VoiceAssistantPanel.css';

const VoiceAssistantPanel = ({ isOpen, onClose }) => {
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');

  // Recording state
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);

  // Conversation state
  const [transcript, setTranscript] = useState([]);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [vadActive, setVadActive] = useState(false);

  // Refs
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioProcessorRef = useRef(null);
  const audioSourceRef = useRef(null);
  const audioStreamRef = useRef(null);
  const sessionIdRef = useRef(null);
  const audioBufferRef = useRef([]);
  const transcriptEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const vadTimeoutRef = useRef(null);
  const lastVadActiveTimeRef = useRef(null);
  const autoProcessingRef = useRef(false);

  // Generate session ID
  const generateSessionId = () => {
    return 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  };

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  // Initialize on mount
  useEffect(() => {
    if (isOpen) {
      initializeVoiceAssistant();
    }

    return () => {
      cleanup();
    };
  }, [isOpen]);

  // Initialize voice assistant
  const initializeVoiceAssistant = async () => {
    try {
      // Generate session ID
      if (!sessionIdRef.current) {
        sessionIdRef.current = generateSessionId();
      }

      // Connect WebSocket
      await connectWebSocket();

      // Request microphone and auto-start listening
      const micSuccess = await setupMicrophone();
      if (micSuccess) {
        setTimeout(() => {
          startListening();
        }, 500); // Small delay to ensure connection is ready
      }
    } catch (error) {
      console.error('Initialization failed:', error);
      addSystemMessage('❌ Failed to initialize voice assistant');
    }
  };

  // Connect to WebSocket
  const connectWebSocket = () => {
    return new Promise((resolve, reject) => {
      try {
        setConnectionStatus('Connecting...');

        const wsUrl = `wss://phone.getscreened.in/marathi/ws?session_id=${sessionIdRef.current}`;
        console.log('🔌 Connecting with session_id:', sessionIdRef.current);

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('✅ WebSocket connected');
          console.log('Session ID:', sessionIdRef.current);
          console.log('WebSocket readyState:', ws.readyState, '(1 = OPEN)');

          // Send init message
          const initMsg = {
            type: 'init',
            session_id: sessionIdRef.current,
            context: 'vehicle_diagnostics',
            timestamp: Date.now()
          };
          console.log('📤 Sending init message:', initMsg);
          ws.send(JSON.stringify(initMsg));

          setIsConnected(true);
          setConnectionStatus('Connected');
          reconnectAttemptsRef.current = 0;
          addSystemMessage('🟢 Voice assistant connected. Starting....');
          resolve();
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            handleWebSocketMessage(data);
          } catch (error) {
            console.error('Message parse error:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          setConnectionStatus('Error');
          addSystemMessage('❌ Connection error');
          reject(error);
        };

        ws.onclose = (event) => {
          console.log('🔌 WebSocket disconnected - Code:', event.code);
          setIsConnected(false);
          setIsListening(false);
          setConnectionStatus('Disconnected');

          // Attempt reconnection
          if (reconnectAttemptsRef.current < 3 && isOpen) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 5000);
            reconnectAttemptsRef.current++;
            reconnectTimeoutRef.current = setTimeout(() => {
              addSystemMessage(`🔄 Reconnecting... (Attempt ${reconnectAttemptsRef.current})`);
              connectWebSocket();
            }, delay);
          }
        };
      } catch (error) {
        console.error('WebSocket creation failed:', error);
        setConnectionStatus('Failed');
        reject(error);
      }
    });
  };

  // Handle WebSocket messages
  const handleWebSocketMessage = (data) => {
    console.log('📨 Message received:', data);

    switch (data.type) {
      case 'ready':
        console.log('✅ Backend ready:', data.message);
        addSystemMessage('✅ ' + (data.message || 'Ready to assist'));
        break;

      case 'vad_status':
        console.log('🎤 VAD Status:', data.is_speech ? 'SPEECH' : 'SILENCE');
        setVadActive(data.is_speech);
        handleVadStatusChange(data.is_speech);
        break;

      case 'transcript':
        console.log('📝 Transcript:', data.text, 'Final:', data.is_final);
        if (data.text && data.is_final !== false) {
          setCurrentTranscript(data.text);
        }
        break;

      case 'processing':
        console.log('⚙️ Backend processing query...');
        addSystemMessage('🤔 Processing...');
        break;

      case 'response':
      case 'ai_response':
        console.log('🤖 AI Response received:', {
          transcript: data.transcript,
          response: data.response || data.text,
          hasAudio: !!data.audio
        });

        // User transcript
        if (data.transcript) {
          addTranscript('user', data.transcript);
          setCurrentTranscript('');
        }

        // AI response
        const responseText = data.response || data.text;
        if (responseText) {
          addTranscript('ai', responseText);
        }

        // Audio playback
        if (data.audio && !isSpeakerMuted) {
          playAudioResponse(data.audio);
        }
        break;

      case 'error':
        console.error('❌ Backend error:', data.message);
        addSystemMessage('⚠️ ' + (data.message || 'An error occurred'));
        break;

      default:
        console.warn('⚠️ Unknown message type:', data.type, data);
    }
  };

  // Handle VAD status changes with auto-processing
  const handleVadStatusChange = (isSpeech) => {
    const VAD_SILENCE_TIMEOUT = 1500; // 1.5 seconds of silence before auto-processing

    if (isSpeech) {
      // Speech detected - cancel any pending auto-processing
      if (vadTimeoutRef.current) {
        clearTimeout(vadTimeoutRef.current);
        vadTimeoutRef.current = null;
      }
      lastVadActiveTimeRef.current = Date.now();
      autoProcessingRef.current = false;
    } else {
      // Speech stopped - start countdown for auto-processing
      if (!isPaused && isListening && !autoProcessingRef.current) {
        // Clear any existing timeout
        if (vadTimeoutRef.current) {
          clearTimeout(vadTimeoutRef.current);
        }

        // Set new timeout for auto-processing
        vadTimeoutRef.current = setTimeout(() => {
          // Check if we had any speech before
          if (lastVadActiveTimeRef.current && (Date.now() - lastVadActiveTimeRef.current) < 10000) {
            console.log('🤖 Auto-processing: Silence detected after speech');
            autoProcessingRef.current = true;
            autoProcessAudio();
          }
        }, VAD_SILENCE_TIMEOUT);
      }
    }
  };

  // Auto-process audio when silence is detected
  const autoProcessAudio = () => {
    if (!isListening || isPaused) return;

    // Send end-of-speech marker
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'end_speech',
        session_id: sessionIdRef.current
      }));
    }

    addSystemMessage('🔄 Processing your query...');

    // Reset auto-processing flag after a short delay
    setTimeout(() => {
      autoProcessingRef.current = false;
      lastVadActiveTimeRef.current = null;
    }, 1000);
  };

  // Setup microphone
  const setupMicrophone = async () => {
    try {
      audioStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      console.log('🎤 Microphone access granted');

      // Create audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000
      });

      return true;
    } catch (error) {
      console.error('❌ Microphone access denied:', error);
      addSystemMessage('❌ Microphone access denied. Please grant permission.');
      return false;
    }
  };

  // Start listening
  const startListening = () => {
    // Prevent multiple startListening calls
    if (isListening) {
      console.log('⚠️ Already listening, skipping...');
      return;
    }

    if (!isConnected || !audioStreamRef.current || !audioContextRef.current) {
      console.error('❌ Cannot start listening:', {
        isConnected,
        hasStream: !!audioStreamRef.current,
        hasContext: !!audioContextRef.current
      });
      addSystemMessage('⚠️ Cannot start listening. Please wait for connection.');
      return;
    }

    // Verify WebSocket is actually open
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('❌ WebSocket not ready:', {
        hasWS: !!wsRef.current,
        state: wsRef.current?.readyState
      });
      addSystemMessage('⚠️ WebSocket not ready. Please wait...');
      return;
    }

    console.log('✅ Starting audio streaming with WebSocket state:', wsRef.current.readyState);

    // Stop any existing audio processor first
    if (audioProcessorRef.current) {
      console.log('🛑 Cleaning up existing audio processor');
      audioProcessorRef.current.disconnect();
      audioProcessorRef.current = null;
    }

    if (audioSourceRef.current) {
      console.log('🛑 Cleaning up existing audio source');
      audioSourceRef.current.disconnect();
      audioSourceRef.current = null;
    }

    try {
      audioSourceRef.current = audioContextRef.current.createMediaStreamSource(audioStreamRef.current);
      audioProcessorRef.current = audioContextRef.current.createScriptProcessor(512, 1, 1);

      let sampleBuffer = [];
      const targetSamples = 160; // 20ms at 16kHz

      let frameCount = 0;

      audioProcessorRef.current.onaudioprocess = (e) => {
        frameCount++;

        // Debug: Log audio processor status every 100 frames
        if (frameCount % 100 === 0) {
          console.log('🎙️ Audio processor running', {
            frameCount,
            isPaused,
            wsState: wsRef.current?.readyState,
            bufferSize: sampleBuffer.length
          });
        }

        if (!isPaused && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const inputData = e.inputBuffer.getChannelData(0);

          for (let i = 0; i < inputData.length; i++) {
            sampleBuffer.push(inputData[i]);

            if (sampleBuffer.length >= targetSamples) {
              const frameSamples = sampleBuffer.slice(0, targetSamples);
              sampleBuffer = sampleBuffer.slice(targetSamples);

              // Convert to PCM Int16
              const pcmData = new Int16Array(targetSamples);
              for (let j = 0; j < targetSamples; j++) {
                const s = Math.max(-1, Math.min(1, frameSamples[j]));
                pcmData[j] = s < 0 ? s * 0x8000 : s * 0x7FFF;
              }

              // Store in buffer if paused, otherwise send immediately
              if (isPaused) {
                audioBufferRef.current.push(pcmData);
              } else {
                sendAudioFrame(pcmData);
              }
            }
          }
        } else {
          // Debug: Log why audio is not being sent
          if (frameCount % 100 === 0) {
            console.warn('⚠️ Audio not sent:', {
              isPaused,
              hasWS: !!wsRef.current,
              wsState: wsRef.current?.readyState,
              expectedState: WebSocket.OPEN
            });
          }
        }
      };

      audioSourceRef.current.connect(audioProcessorRef.current);
      audioProcessorRef.current.connect(audioContextRef.current.destination);

      setIsListening(true);
      setIsPaused(false);
      addSystemMessage('🎤 Listening... Speak now');
      console.log('🔊 Audio streaming started');
    } catch (error) {
      console.error('Failed to start listening:', error);
      addSystemMessage('❌ Failed to start listening');
    }
  };

  // Send audio frame
  const sendAudioFrame = (pcmData) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const uint8Array = new Uint8Array(pcmData.buffer);
      const base64Audio = btoa(String.fromCharCode.apply(null, uint8Array));

      // Debug: Log every 50th frame to avoid console spam
      if (Math.random() < 0.02) {
        console.log('📡 Sending audio frame, size:', base64Audio.length, 'bytes');
      }

      wsRef.current.send(JSON.stringify({
        type: 'audio_frame',
        data: base64Audio,
        session_id: sessionIdRef.current
      }));
    } else {
      console.warn('⚠️ Cannot send audio: WebSocket not ready', wsRef.current?.readyState);
    }
  };

  // Pause listening
  const pauseListening = () => {
    setIsPaused(true);
    audioBufferRef.current = []; // Clear buffer
    addSystemMessage('⏸️ Paused. Click Send to process or Resume to continue.');
  };

  // Resume listening
  const resumeListening = () => {
    setIsPaused(false);
    audioBufferRef.current = []; // Clear any buffered audio
    addSystemMessage('▶️ Resumed listening');
  };

  // Send buffered audio
  const sendBufferedAudio = () => {
    if (audioBufferRef.current.length > 0) {
      addSystemMessage('📤 Sending audio...');

      // Send all buffered frames
      audioBufferRef.current.forEach(pcmData => {
        sendAudioFrame(pcmData);
      });

      // Clear buffer
      audioBufferRef.current = [];

      // Send end-of-speech marker
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'end_speech',
          session_id: sessionIdRef.current
        }));
      }

      addSystemMessage('✅ Audio sent. Processing...');
    }

    // Resume listening
    setIsPaused(false);
  };

  // Stop listening
  const stopListening = () => {
    console.log('🛑 Stopping listening...');

    if (audioProcessorRef.current) {
      audioProcessorRef.current.disconnect();
      audioProcessorRef.current = null;
    }

    if (audioSourceRef.current) {
      audioSourceRef.current.disconnect();
      audioSourceRef.current = null;
    }

    setIsListening(false);
    setIsPaused(false);
    audioBufferRef.current = [];
    addSystemMessage('🛑 Stopped listening');
  };

  // Play audio response
  const playAudioResponse = async (base64Audio) => {
    try {
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const audioBuffer = await audioContextRef.current.decodeAudioData(bytes.buffer);
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      source.start(0);
    } catch (error) {
      console.error('Audio playback error:', error);
    }
  };

  // Add transcript
  const addTranscript = (role, text) => {
    setTranscript(prev => [...prev, {
      role,
      text,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  // Add system message
  const addSystemMessage = (text) => {
    setTranscript(prev => [...prev, {
      role: 'system',
      text,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  // Toggle speaker
  const toggleSpeaker = () => {
    setIsSpeakerMuted(!isSpeakerMuted);
    addSystemMessage(isSpeakerMuted ? '🔊 Speaker unmuted' : '🔇 Speaker muted');
  };

  // Cleanup
  const cleanup = () => {
    console.log('🧹 Cleaning up voice assistant...');

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (vadTimeoutRef.current) {
      clearTimeout(vadTimeoutRef.current);
      vadTimeoutRef.current = null;
    }

    if (audioProcessorRef.current) {
      audioProcessorRef.current.disconnect();
      audioProcessorRef.current = null;
    }

    if (audioSourceRef.current) {
      audioSourceRef.current.disconnect();
      audioSourceRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'control',
        data: 'end',
        session_id: sessionIdRef.current
      }));
      wsRef.current.close();
    }

    audioBufferRef.current = [];
    sessionIdRef.current = null;
    lastVadActiveTimeRef.current = null;
    autoProcessingRef.current = false;
  };

  // Handle close
  const handleClose = () => {
    cleanup();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={`voice-panel-overlay ${isOpen ? 'open' : ''}`}>
      <div className="voice-assistant-panel glass-panel">
        {/* Header */}
        <div className="voice-panel-header">
          <div className="voice-panel-title">
            <Volume2 size={28} color="var(--accent-primary)" />
            <div>
              <h2>Voice Assistant</h2>
              <p className="voice-panel-subtitle">MHI Assistant · diagnostics</p>
            </div>
          </div>
          <button className="voice-panel-close" onClick={handleClose} title="Close">
            <X size={24} />
          </button>
        </div>

        {/* Connection Status */}
        <div className={`connection-banner ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? (
            <>
              <span className="status-dot pulse"></span>
              <span>{connectionStatus}</span>
            </>
          ) : (
            <>
              <WifiOff size={16} />
              <span>{connectionStatus}</span>
            </>
          )}
        </div>

        {/* Listening Status */}
        {isListening && (
          <div className="listening-banner">
            <div className="voice-waves-banner">
              <div className={`wave-bar ${vadActive ? 'active' : ''}`}></div>
              <div className={`wave-bar ${vadActive ? 'active' : ''}`}></div>
              <div className={`wave-bar ${vadActive ? 'active' : ''}`}></div>
              <div className={`wave-bar ${vadActive ? 'active' : ''}`}></div>
              <div className={`wave-bar ${vadActive ? 'active' : ''}`}></div>
            </div>
            <span className="listening-text">
              {isPaused ? '⏸️ Paused - Click Send to process' : '🎤 Listening...'}
            </span>
          </div>
        )}

        {/* Current Transcript (Real-time) */}
        {currentTranscript && (
          <div className="current-transcript">
            <span className="transcript-label">You're saying:</span>
            <p>{currentTranscript}</p>
          </div>
        )}

        {/* Transcript History */}
        <div className="voice-transcript">
          {transcript.length === 0 ? (
            <div className="transcript-empty">
              <Mic size={48} color="var(--text-muted)" />
              <p>No conversation yet</p>
              <span>Start speaking to begin</span>
            </div>
          ) : (
            <>
              {transcript.map((entry, i) => (
                <div key={i} className={`transcript-entry transcript-${entry.role}`}>
                  <div className="transcript-avatar">
                    {entry.role === 'user' && '👤'}
                    {entry.role === 'ai' && '🤖'}
                    {entry.role === 'system' && 'ℹ️'}
                  </div>
                  <div className="transcript-bubble">
                    <div className="transcript-text">{entry.text}</div>
                    <div className="transcript-timestamp">{entry.timestamp}</div>
                  </div>
                </div>
              ))}
              <div ref={transcriptEndRef} />
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="voice-action-panel">
          <div className="voice-actions-row">
            {/* Microphone Toggle */}
            <button
              className={`voice-action-btn ${isListening && !isPaused ? 'active' : ''}`}
              onClick={isListening ? stopListening : startListening}
              disabled={!isConnected}
              title={isListening ? 'Stop Listening' : 'Start Listening'}
            >
              {isListening ? <MicOff size={24} /> : <Mic size={24} />}
              <span>{isListening ? 'Stop' : 'Start'}</span>
            </button>

            {/* Pause/Resume */}
            {isListening && (
              <button
                className={`voice-action-btn ${isPaused ? 'paused' : ''}`}
                onClick={isPaused ? resumeListening : pauseListening}
                title={isPaused ? 'Resume' : 'Pause'}
              >
                {isPaused ? <Play size={24} /> : <Pause size={24} />}
                <span>{isPaused ? 'Resume' : 'Pause'}</span>
              </button>
            )}

            {/* Send (only when paused) */}
            {isPaused && (
              <button
                className="voice-action-btn send-btn"
                onClick={sendBufferedAudio}
                title="Send Audio"
              >
                <Send size={24} />
                <span>Send</span>
              </button>
            )}

            {/* Speaker Toggle */}
            <button
              className={`voice-action-btn ${isSpeakerMuted ? 'muted' : ''}`}
              onClick={toggleSpeaker}
              disabled={!isConnected}
              title={isSpeakerMuted ? 'Unmute Speaker' : 'Mute Speaker'}
            >
              {isSpeakerMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
              <span>{isSpeakerMuted ? 'Unmuted' : 'Speaker'}</span>
            </button>
          </div>

          <div className="voice-hint">
            💡 Tip: Speak naturally. Your query will auto-process after 1.5s of silence.
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceAssistantPanel;
